/**
 * Settings → Usage: the two limit cards, the plan card, and a compact "Last 7
 * days" table by model — from the prototype's usage tab (lines ~308-320) plus
 * the per-model breakdown `api.getUsage()` adds.
 */

import { useEffect, useState } from 'react';
import { getUsage, type ModelInfo, type UsageMonth, type UsageReport, type UsageWindow } from '../../api';
import { useApp } from '../../state';
import { MonoLabel } from '../ui';
import { ds } from '../../ds';
import { UsageBar } from '../composer/Usage';
import { poundsLabel, windowLabel } from '../composer/usageText';
import { formatTokens, resetText } from './format';

function LimitCard({ label, usage: w }: { label: string; usage: UsageWindow }) {
  return (
    <div
      style={{
        background: 'var(--surface-card)',
        borderRadius: 'var(--radius-app-card)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10
      }}
    >
      <MonoLabel>{label}</MonoLabel>
      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 36, lineHeight: 1, color: 'var(--text-strong)' }}>
        {windowLabel(w)}
      </span>
      {w.allowance !== null ? <UsageBar pct={w.pct} used={w.used} /> : null}
      {w.resetsAt !== null && w.allowance !== null ? (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{resetText(w.resetsAt)}</span>
      ) : null}
    </div>
  );
}

/** An organisation's month: what has been spent, and against what. */
function MonthCard({ month }: { month: UsageMonth }) {
  return (
    <div
      style={{
        background: 'var(--surface-card)',
        borderRadius: 'var(--radius-app-card)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10
      }}
    >
      <MonoLabel>This month</MonoLabel>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 36, lineHeight: 1, color: 'var(--text-strong)' }}>
          {poundsLabel(month.spentPence)}
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {month.limitPence !== null ? `of ${poundsLabel(month.limitPence)}` : 'No limit'}
        </span>
      </span>
      {month.limitPence !== null ? <UsageBar pct={month.pct ?? 0} used={month.spentPence} /> : null}
      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{resetText(month.resetsAt)}</span>
    </div>
  );
}

export function UsageTab() {
  const { me } = useApp();
  const { Button } = ds();
  const [report, setReport] = useState<UsageReport | null>(null);

  useEffect(() => {
    let cancelled = false;
    getUsage()
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch(() => {
        // The two summary cards still draw from `me.usage`; the plan card and
        // the table need the report and simply do not appear without it.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!me) return null;
  const summary = report?.summary ?? me.usage;
  const plan = report?.plan ?? me.plan;
  const planLabel = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {summary.month ? (
          <MonthCard month={summary.month} />
        ) : (
          <>
            <LimitCard label="5-hour limit" usage={summary.fiveHour} />
            <LimitCard label="Weekly" usage={summary.weekly} />
          </>
        )}
      </div>

      <div
        style={{
          background: 'var(--surface-card)',
          borderRadius: 'var(--radius-app-card)',
          padding: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap'
        }}
      >
        <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
            {summary.month ? 'Billed to your organisation' : `Assist ${planLabel}`}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Local models and your own API keys don't count towards limits.
          </span>
        </div>
        {summary.month || plan === 'developer' ? null : (
          <Button variant="neutral" density="app" size="sm" href={`${me.accountUrl}/account/billing`}>
            Change plan
          </Button>
        )}
      </div>

      {report && report.byModel.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <MonoLabel>Last 7 days</MonoLabel>
          <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr) auto auto',
                gap: 16,
                padding: '8px 16px',
                fontSize: 11.5,
                color: 'var(--text-faint)'
              }}
            >
              <span>Model</span>
              <span style={{ textAlign: 'right' }}>In</span>
              <span style={{ textAlign: 'right' }}>Out</span>
            </div>
            {report.byModel.map((row) => (
              <ModelRow key={row.model} model={row.model} tokensIn={row.tokensIn} tokensOut={row.tokensOut} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The models Assist runs on its own account — titles, suggestions, images,
 *  dictation — which the chat menu never offers, so the catalogue has no name
 *  for them. */
const SIDE_MODELS: Record<string, string> = {
  'workers-ai:@cf/meta/llama-3.1-8b-instruct-fp8': 'Titles',
  'workers-ai:@cf/black-forest-labs/flux-1-schnell': 'Images',
  'workers-ai:@cf/openai/whisper-large-v3-turbo': 'Dictation',
  'workers-ai:@cf/deepgram/nova-3': 'Dictation · Nova-3',
  'workers-ai:@cf/deepgram/flux': 'Voice · listening',
  'workers-ai:@cf/deepgram/aura-2-en': 'Voice · speaking',
  'workers-ai:@cf/black-forest-labs/flux-2-dev': 'Images · FLUX.2 [dev]',
  'workers-ai:@cf/black-forest-labs/flux-2-klein-4b': 'Images · FLUX.2 [klein] 4B',
  'workers-ai:@cf/black-forest-labs/flux-2-klein-9b': 'Images · FLUX.2 [klein] 9B',
  'workers-ai:@cf/leonardo/lucid-origin': 'Images · Lucid Origin',
  'workers-ai:@cf/leonardo/phoenix-1.0': 'Images · Phoenix 1.0',
  'workers-ai:openai/gpt-image-2.5-sunburst': 'Images · GPT Image 2.5',
  'workers-ai:google/nano-banana-pro': 'Images · Nano Banana Pro',
  'workers-ai:google/nano-banana-2': 'Images · Nano Banana 2',
  'tavily:search': 'Web search',
  'tavily:research': 'Research'
};

/** `google:gemini-2.5-flash` → `gemini-2.5-flash`; `workers-ai:@cf/x/y` → `y`. */
const shortId = (id: string): string => id.slice(id.indexOf(':') + 1).split('/').pop() ?? id;

/** A model as the usage tables name it: the menu's name, a side model's, or
 *  the last part of its id. */
export const usageModelName = (id: string, models: readonly ModelInfo[] | undefined): string =>
  models?.find((m) => m.id === id)?.name ?? SIDE_MODELS[id] ?? shortId(id);

function ModelRow({ model, tokensIn, tokensOut }: { model: string; tokensIn: number; tokensOut: number }) {
  const { catalogue } = useApp();
  const name = usageModelName(model, catalogue?.models);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) auto auto',
        alignItems: 'center',
        gap: 16,
        minHeight: 40,
        padding: '0 16px',
        borderTop: '1px solid var(--border-subtle)',
        fontSize: 13.5
      }}
    >
      <span style={{ color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-body)', textAlign: 'right' }}>
        {formatTokens(tokensIn)}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-body)', textAlign: 'right' }}>
        {formatTokens(tokensOut)}
      </span>
    </div>
  );
}
